import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Clock, FileSpreadsheet, Loader2, RefreshCw, Save, Warehouse } from 'lucide-react';
import { warehouseService } from '../services/warehouseService';
import { shiftService } from '../services/shiftService';
import { toast } from 'sonner';
import * as XLSX from 'xlsx-js-style';

type WarehouseItem = { id: string; name: string; code?: string };
type ShiftItem = { id: string; name: string; code: string; start_time?: string; end_time?: string; status?: string };
type RampAssignment = {
  id: string;
  shipmentNo: string;
  planCode: string;
  orderCode: string;
  vehiclePlate: string;
  driverName: string;
  operationType: 'loading' | 'unloading';
  operationLabel: string;
  pointName: string;
  startTime: string;
  endTime: string;
  startMinutes: number;
  endMinutes: number;
  durationMinutes: number;
};

type RampPlan = {
  id: string;
  rampNumber: string;
  name: string;
  type: 'loading' | 'unloading' | 'both';
  assignments: RampAssignment[];
};

export function WarehouseRampAssignment() {
  const [warehouses, setWarehouses] = useState<WarehouseItem[]>([]);
  const [shifts, setShifts] = useState<ShiftItem[]>([]);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState('');
  const [selectedShiftId, setSelectedShiftId] = useState('');
  const [loading, setLoading] = useState(true);
  const [planningLoading, setPlanningLoading] = useState(false);
  const [planning, setPlanning] = useState<any>(null);
  const [draggingAssignmentId, setDraggingAssignmentId] = useState<string | null>(null);
  const [savingPlanning, setSavingPlanning] = useState(false);

  useEffect(() => {
    const loadSources = async () => {
      try {
        setLoading(true);
        const [warehousesRes, shiftsRes] = await Promise.all([
          warehouseService.getWarehouses(),
          shiftService.getShifts(),
        ]);
        const warehouseItems = Array.isArray(warehousesRes?.data) ? warehousesRes.data : [];
        const shiftItems = Array.isArray(shiftsRes?.data) ? shiftsRes.data : Array.isArray(shiftsRes?.data?.data) ? shiftsRes.data.data : [];
        setWarehouses(warehouseItems.map((item: any) => ({ id: String(item.id), name: item.name || '-', code: item.code || '' })));
        setShifts(shiftItems.filter((item: any) => String(item.status || 'active') !== 'inactive').map((item: any) => ({
          id: String(item.id),
          name: item.name || '-',
          code: item.code || '-',
          start_time: item.start_time || item.startTime || '',
          end_time: item.end_time || item.endTime || '',
          status: item.status || 'active',
        })));
        if (warehouseItems[0]) setSelectedWarehouseId(String(warehouseItems[0].id));
        if (shiftItems[0]) setSelectedShiftId(String(shiftItems[0].id));
      } catch (error) {
        console.error('Ramp assignment source load failed:', error);
        toast.error('Depo ve vardiya verileri yüklenemedi');
      } finally {
        setLoading(false);
      }
    };
    void loadSources();
  }, []);

  const loadPlanning = async () => {
    if (!selectedWarehouseId || !selectedShiftId) return;
    try {
      setPlanningLoading(true);
      const response: any = await warehouseService.getRampPlanning({ warehouseId: selectedWarehouseId, shiftId: selectedShiftId });
      setPlanning(response?.data || null);
    } catch (error) {
      console.error('Ramp planning load failed:', error);
      toast.error('Rampa planı yüklenemedi');
      setPlanning(null);
    } finally {
      setPlanningLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedWarehouseId || !selectedShiftId) return;
    void loadPlanning();
  }, [selectedWarehouseId, selectedShiftId]);

  const timeMeta = useMemo(() => {
    const start = planning?.shift?.startTime || '08:00';
    const end = planning?.shift?.endTime || '18:00';
    const [sh, sm] = String(start).split(':').map(Number);
    const [eh, em] = String(end).split(':').map(Number);
    const startMinutes = (sh * 60) + sm;
    let endMinutes = (eh * 60) + em;
    if (endMinutes <= startMinutes) endMinutes = startMinutes + (10 * 60);
    const totalMinutes = endMinutes - startMinutes;
    const ticks: Array<{ label: string; minute: number }> = [];
    for (let minute = startMinutes; minute <= endMinutes; minute += 60) {
      const hour = Math.floor(minute / 60);
      const min = minute % 60;
      ticks.push({ label: `${String(hour).padStart(2, '0')}:${String(min).padStart(2, '0')}`, minute });
    }
    return { startMinutes, endMinutes, totalMinutes, ticks };
  }, [planning]);

  const pxPerMinute = 2;
  const timelineWidth = Math.max(900, timeMeta.totalMinutes * pxPerMinute);
  const formatMinutes = (minutes: number) => `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;

  const savePlanning = async (nextPlanning = planning) => {
    if (!nextPlanning || !selectedWarehouseId || !selectedShiftId) return;
    try {
      setSavingPlanning(true);
      await warehouseService.saveRampPlanning({ warehouseId: selectedWarehouseId, shiftId: selectedShiftId, ramps: nextPlanning.ramps || [] });
      toast.success('Rampa planı kaydedildi');
    } catch (error) {
      console.error('Save ramp planning failed:', error);
      toast.error('Rampa planı kaydedilemedi');
    } finally {
      setSavingPlanning(false);
    }
  };

  const exportExcel = () => {
    if (!planning?.ramps?.length) return;
    const rows = planning.ramps.flatMap((ramp: RampPlan) => ramp.assignments.map((assignment) => ({
      Rampa: ramp.name,
      Tür: assignment.operationLabel,
      'Sevkiyat No': assignment.shipmentNo,
      'Plan No': assignment.planCode,
      'Sipariş No': assignment.orderCode,
      'Çekici': assignment.vehiclePlate,
      Sürücü: assignment.driverName,
      Nokta: assignment.pointName,
      Başlangıç: assignment.startTime,
      Bitiş: assignment.endTime,
    })));
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Rampa Planı');
    XLSX.writeFile(workbook, `rampa-plani-${selectedWarehouseId}-${selectedShiftId}.xlsx`);
  };

  const handleDropToRamp = (targetRampId: string) => {
    if (!draggingAssignmentId || !planning?.ramps) return;
    const nextRamps: RampPlan[] = (planning.ramps as RampPlan[]).map((ramp) => ({ ...ramp, assignments: [...ramp.assignments] }));
    let dragged: RampAssignment | null = null;
    for (const ramp of nextRamps) {
      const idx = ramp.assignments.findIndex((item) => item.id === draggingAssignmentId);
      if (idx >= 0) {
        dragged = ramp.assignments.splice(idx, 1)[0];
        break;
      }
    }
    if (!dragged) return;
    const targetRamp = nextRamps.find((ramp) => String(ramp.id) === String(targetRampId));
    if (!targetRamp) return;
    const supportsType = targetRamp.type === 'both' || targetRamp.type === dragged.operationType;
    if (!supportsType) {
      toast.error('Bu rampa bu operasyon tipini desteklemiyor');
      return;
    }
    targetRamp.assignments.push(dragged);
    const recalc = nextRamps.map((ramp) => {
      let cursor = timeMeta.startMinutes;
      const assignments = ramp.assignments.map((assignment) => {
        const next = {
          ...assignment,
          startMinutes: cursor,
          endMinutes: cursor + Number(planning.durationMinutes || assignment.durationMinutes || 60),
        };
        next.startTime = formatMinutes(next.startMinutes);
        next.endTime = formatMinutes(next.endMinutes);
        cursor = next.endMinutes;
        return next;
      });
      return { ...ramp, assignments };
    });
    const shipmentKeys = recalc.flatMap((ramp) => ramp.assignments.map((assignment) => `${assignment.id}__${assignment.operationType}`));
    const duplicates = shipmentKeys.filter((key, index) => shipmentKeys.indexOf(key) !== index);
    const nextPlanning = { ...planning, ramps: recalc, conflicts: [...new Set(duplicates)] };
    setPlanning(nextPlanning);
    setDraggingAssignmentId(null);
    void savePlanning(nextPlanning);
  };

  if (loading) {
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>;
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Rampa Atama</h1>
          <p className="mt-1 text-sm text-gray-600">Depo ve vardiyaya göre sevkiyatları aktif rampalara dengeli olarak dağıtan zaman çizelgesi.</p>
        </div>
        <button type="button" onClick={() => void loadPlanning()} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50">
          <RefreshCw className={`h-4 w-4 ${planningLoading ? 'animate-spin' : ''}`} />
          Yenile
        </button>
        <div className="flex items-center gap-2">
          <button type="button" onClick={exportExcel} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50">
            <FileSpreadsheet className="h-4 w-4" /> Excele Aktar
          </button>
          <button type="button" onClick={() => window.print()} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50">
            PDF / Yazdır
          </button>
          <button type="button" onClick={() => void savePlanning()} disabled={savingPlanning || !planning} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50">
            <Save className="h-4 w-4" /> {savingPlanning ? 'Kaydediliyor...' : 'Planı Kaydet'}
          </button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700">Depo / Yükleme Noktası</label>
          <select value={selectedWarehouseId} onChange={(e) => setSelectedWarehouseId(e.target.value)} className="h-11 w-full rounded-xl border border-gray-300 bg-white px-4 text-sm text-gray-800 outline-none focus:border-blue-500">
            <option value="">Depo Seçin</option>
            {warehouses.map((item) => <option key={item.id} value={item.id}>{item.code ? `${item.code} - ${item.name}` : item.name}</option>)}
          </select>
        </div>
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700">Vardiya</label>
          <select value={selectedShiftId} onChange={(e) => setSelectedShiftId(e.target.value)} className="h-11 w-full rounded-xl border border-gray-300 bg-white px-4 text-sm text-gray-800 outline-none focus:border-blue-500">
            <option value="">Vardiya Seçin</option>
            {shifts.map((item) => <option key={item.id} value={item.id}>{`${item.code} - ${item.name}`}</option>)}
          </select>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="text-xs text-slate-500">Rampa Süresi</div>
          <div className="mt-2 text-2xl font-bold text-slate-900">{planning?.durationMinutes || 0} dk</div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="text-xs text-slate-500">Toplam Sevkiyat</div>
          <div className="mt-2 text-2xl font-bold text-slate-900">{planning?.totals?.shipmentCount || 0}</div>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        {Array.isArray(planning?.conflicts) && planning.conflicts.length > 0 && (
          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 inline-flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" /> Aynı sevkiyat birden fazla rampa/operasyon satırında görünüyor. Lütfen planı kontrol edin.
          </div>
        )}
        {!planning ? (
          <div className="py-16 text-center text-sm text-slate-500">Plan göstermek için depo ve vardiya seçin.</div>
        ) : planningLoading ? (
          <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>
        ) : (
          <div className="overflow-auto">
            <div className="min-w-[1200px]">
              <div className="mb-4 flex items-center justify-between">
                <div className="text-sm font-semibold text-slate-900 inline-flex items-center gap-2"><Warehouse className="h-4 w-4" /> {planning?.warehouse?.name || '-'}</div>
                <div className="text-sm text-slate-600 inline-flex items-center gap-2"><Clock className="h-4 w-4" /> {planning?.shift?.code} - {planning?.shift?.name} ({planning?.shift?.startTime} - {planning?.shift?.endTime})</div>
              </div>

              <div className="grid grid-cols-[220px_minmax(0,1fr)] gap-0 border border-slate-200">
                <div className="border-r border-slate-200 bg-slate-50">
                  <div className="h-12 border-b border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700">Rampalar</div>
                  {(planning.ramps || []).map((ramp: RampPlan) => (
                    <div key={ramp.id} className="flex h-24 items-center border-b border-slate-200 px-4">
                      <div>
                        <div className="font-semibold text-slate-900">{ramp.name}</div>
                        <div className="text-xs text-slate-500">{ramp.type === 'both' ? 'Yükleme + Boşaltma' : ramp.type === 'loading' ? 'Yükleme' : 'Boşaltma'}</div>
                      </div>
                    </div>
                  ))}
                </div>

                <div>
                  <div className="relative h-12 border-b border-slate-200 bg-slate-50" style={{ width: timelineWidth }}>
                    {timeMeta.ticks.map((tick) => {
                      const left = (tick.minute - timeMeta.startMinutes) * pxPerMinute;
                      return (
                        <div key={tick.label} className="absolute top-0 h-full border-l border-slate-200" style={{ left }}>
                          <div className="-translate-x-1/2 pt-3 text-xs font-medium text-slate-600">{tick.label}</div>
                        </div>
                      );
                    })}
                  </div>

                  {(planning.ramps || []).map((ramp: RampPlan) => (
                    <div key={`lane-${ramp.id}`} className="relative h-24 border-b border-slate-200 bg-white" style={{ width: timelineWidth }} onDragOver={(e) => e.preventDefault()} onDrop={() => handleDropToRamp(String(ramp.id))}>
                      {timeMeta.ticks.map((tick) => {
                        const left = (tick.minute - timeMeta.startMinutes) * pxPerMinute;
                        return <div key={`grid-${ramp.id}-${tick.label}`} className="absolute top-0 h-full border-l border-slate-100" style={{ left }} />;
                      })}
                      {ramp.assignments.map((assignment) => {
                        const left = (assignment.startMinutes - timeMeta.startMinutes) * pxPerMinute;
                        const width = Math.max(140, (assignment.endMinutes - assignment.startMinutes) * pxPerMinute);
                        return (
                          <div
                            key={`${ramp.id}-${assignment.id}-${assignment.operationType}`}
                            draggable
                            onDragStart={() => setDraggingAssignmentId(assignment.id)}
                            onDragEnd={() => setDraggingAssignmentId(null)}
                            className={`absolute top-2 rounded-xl border px-3 py-2 text-xs shadow-sm cursor-move ${assignment.operationType === 'loading' ? 'border-sky-300 bg-sky-100 text-sky-950' : 'border-orange-300 bg-orange-100 text-orange-950'} ${draggingAssignmentId === assignment.id ? 'opacity-60' : ''}`}
                            style={{ left, width, height: 80 }}
                            title={`${assignment.operationLabel} | ${assignment.vehiclePlate} | ${assignment.shipmentNo} | ${assignment.planCode}`}
                          >
                            <div className="font-semibold truncate">{assignment.vehiclePlate || '-'}</div>
                            <div className="truncate">{assignment.shipmentNo} / {assignment.planCode || '-'}</div>
                            <div className="truncate">{assignment.driverName || '-'}</div>
                            <div className="truncate">{assignment.pointName || '-'}</div>
                            <div className="mt-1 text-[11px] font-medium">{assignment.startTime} - {assignment.endTime}</div>
                          </div>
                        );
                      })}
                    </div>
                  ))}

                  <div className="relative h-12 bg-slate-50" style={{ width: timelineWidth }}>
                    {timeMeta.ticks.map((tick) => {
                      const left = (tick.minute - timeMeta.startMinutes) * pxPerMinute;
                      return (
                        <div key={`bottom-${tick.label}`} className="absolute top-0 h-full border-l border-slate-200" style={{ left }}>
                          <div className="-translate-x-1/2 pt-3 text-xs font-medium text-slate-600">{tick.label}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
